import { renderVisitRequestWorkflowStepAsAction } from '@/types/visitRequest';
import { gql, useQuery } from '@apollo/client';
import { Tab, Tabs } from '@mui/material';
import {
    VisitFilter,
    VisitOutcome,
    VisitRequestFilter,
    VisitRequestState,
    VisitRequestWorkflowStep,
    VisitState,
    VisitWorkflowStep,
} from 'gql-types';
import { useMemo } from 'react';

import { useVisitRequestListPageContext } from '../VisitRequestsListPageContext';
import { QueueTab } from './types';

// Associated filters for each queue tab.
export const VisitRequestQueueTabFilters: Record<QueueTab, VisitRequestFilter> = {
    [QueueTab.NEED_SERVICES]: {
        state: { exact: VisitRequestState.PENDING },
        workflowStep: { exact: VisitRequestWorkflowStep.NEEDS_SERVICES },
    },
    [QueueTab.NEED_CLINICIANS]: {
        state: { exact: VisitRequestState.PENDING },
        workflowStep: { exact: VisitRequestWorkflowStep.NEEDS_CLINICIAN },
    },
    [QueueTab.NEED_TENTATIVE_DATE]: {
        state: { exact: VisitRequestState.PENDING },
        workflowStep: { exact: VisitRequestWorkflowStep.NEEDS_TENTATIVE_DATE },
    },
    [QueueTab.READY_FOR_PROMOTION]: {
        state: { exact: VisitRequestState.PENDING },
        workflowStep: { exact: VisitRequestWorkflowStep.READY_FOR_PROMOTION },
    },
    [QueueTab.ONE_OFF_VISIT_REQUESTS]: {
        state: { exact: VisitRequestState.PENDING },
        hasOrder: { exact: false },
    },
    [QueueTab.FULFILLED]: {
        state: { exact: VisitRequestState.FULFILLED },
    },
    [QueueTab.CANCELED]: {
        state: { exact: VisitRequestState.CANCELED },
    },
};

export type ViewFilters = {
    visit: VisitFilter | 'exclude';
    visitRequest: VisitRequestFilter | 'exclude';
};

export const VIEW_FILTERS = {
    [VisitRequestWorkflowStep.NEEDS_SERVICES]: {
        visit: {
            workflowStep: { exact: VisitWorkflowStep.NEEDS_SERVICE },
            outcome: { exact: VisitOutcome.NONE },
        },
        visitRequest: {
            state: { exact: VisitRequestState.PENDING },
            workflowStep: { exact: VisitRequestWorkflowStep.NEEDS_SERVICES },
        },
    },
    [VisitRequestWorkflowStep.NEEDS_CLINICIAN]: {
        visit: {
            workflowStep: { exact: VisitWorkflowStep.NEEDS_CLINICIAN },
            outcome: { exact: VisitOutcome.NONE },
        },
        visitRequest: {
            state: { exact: VisitRequestState.PENDING },
            workflowStep: { exact: VisitRequestWorkflowStep.NEEDS_CLINICIAN },
        },
    },
    [VisitRequestWorkflowStep.NEEDS_TENTATIVE_DATE]: {
        visit: {
            workflowStep: { exact: VisitWorkflowStep.NEEDS_DATE },
            outcome: { exact: VisitOutcome.NONE },
        },
        visitRequest: {
            state: { exact: VisitRequestState.PENDING },
            workflowStep: { exact: VisitRequestWorkflowStep.NEEDS_TENTATIVE_DATE },
        },
    },
    [VisitRequestWorkflowStep.READY_FOR_PROMOTION]: {
        visitRequest: {
            state: { exact: VisitRequestState.PENDING },
            workflowStep: { exact: VisitRequestWorkflowStep.READY_FOR_PROMOTION },
        },
        visit: 'exclude',
    },
    [VisitState.UNSCHEDULED]: {
        visit: {
            state: { exact: VisitState.UNSCHEDULED },
        },
        visitRequest: 'exclude',
    },
    [VisitState.SCHEDULED]: {
        visit: {
            state: { exact: VisitState.SCHEDULED },
        },
        visitRequest: 'exclude',
    },
    [VisitState.CONFIRMED]: {
        visit: {
            state: { exact: VisitState.CONFIRMED },
        },
        visitRequest: 'exclude',
    },
    [VisitState.EN_ROUTE]: {
        visit: {
            state: { exact: VisitState.EN_ROUTE },
        },
        visitRequest: 'exclude',
    },
    [VisitState.AT_LOCATION]: {
        visit: {
            state: { exact: VisitState.AT_LOCATION },
        },
        visitRequest: 'exclude',
    },
    [VisitState.IN_REVIEW]: {
        visit: {
            state: { exact: VisitState.IN_REVIEW },
        },
        visitRequest: 'exclude',
    },
    [VisitState.ENDED]: {
        visit: {
            state: { exact: VisitState.ENDED },
        },
        visitRequest: 'exclude',
    },
} as const;

// Query that counts the number of visit requests for each tab.
export const VISIT_REQUESTS_WORKFLOW_TAB_COUNTS_QUERY = gql`
    query VisitRequestsWorkflowTabCounts(
        $needsServiceFilters: VisitRequestFilter
        $needsCliniciansFilters: VisitRequestFilter
        $needsTentativeDateFilters: VisitRequestFilter
        $readyForPromotionFilters: VisitRequestFilter
        $fulfilledFilters: VisitRequestFilter
        $oneOffFilters: VisitRequestFilter
        $canceledFilters: VisitRequestFilter
        $allFilters: VisitRequestFilter
    ) {
        needsServices: visitRequests(filters: $needsServiceFilters) {
            totalCount
        }
        needsClinicians: visitRequests(filters: $needsCliniciansFilters) {
            totalCount
        }
        needsTentativeDate: visitRequests(filters: $needsTentativeDateFilters) {
            totalCount
        }
        readyForPromotion: visitRequests(filters: $readyForPromotionFilters) {
            totalCount
        }
        fulfilled: visitRequests(filters: $fulfilledFilters) {
            totalCount
        }
        oneOffs: visitRequests(filters: $oneOffFilters) {
            totalCount
        }
        canceled: visitRequests(filters: $canceledFilters) {
            totalCount
        }
        all: visitRequests(filters: $allFilters) {
            totalCount
        }
    }
`;

const usePerTabCounts = ({ additionalFilters }: { additionalFilters: VisitRequestFilter }) => {
    const perTabCountFilters = useMemo(() => {
        const baseFilters: VisitRequestFilter = {
            ...additionalFilters,
        };

        return {
            needsServicesFilters: {
                ...baseFilters,
                ...VisitRequestQueueTabFilters[QueueTab.NEED_SERVICES],
            },
            needsCliniciansFilters: {
                ...baseFilters,
                ...VisitRequestQueueTabFilters[QueueTab.NEED_CLINICIANS],
            },
            needsTentativeDateFilters: {
                ...baseFilters,
                ...VisitRequestQueueTabFilters[QueueTab.NEED_TENTATIVE_DATE],
            },
            readyForPromotionFilters: {
                ...baseFilters,
                ...VisitRequestQueueTabFilters[QueueTab.READY_FOR_PROMOTION],
            },
            oneOffFilters: {
                ...baseFilters,
                ...VisitRequestQueueTabFilters[QueueTab.ONE_OFF_VISIT_REQUESTS],
            },
            fulfilledFilters: {
                ...baseFilters,
                ...VisitRequestQueueTabFilters[QueueTab.FULFILLED],
            },
            canceledFilters: {
                ...baseFilters,
                ...VisitRequestQueueTabFilters[QueueTab.CANCELED],
            },
            allFilters: baseFilters,
        };
    }, [additionalFilters]);

    const { data, previousData } = useQuery<{
        needsServices: { totalCount: number };
        needsClinicians: { totalCount: number };
        needsTentativeDate: { totalCount: number };
        readyForPromotion: { totalCount: number };
        fulfilled: { totalCount: number };
        canceled: { totalCount: number };
        oneOffs: { totalCount: number };
    }>(VISIT_REQUESTS_WORKFLOW_TAB_COUNTS_QUERY, {
        variables: perTabCountFilters,
        // TODO(jessica): Change this when our cache updating via mutations behavior
        // is more refined.
        fetchPolicy: 'cache-and-network',
    });

    const workflowTabCounts = data ?? previousData;

    return useMemo(() => {
        if (!workflowTabCounts) {
            return null;
        }
        const {
            needsServices,
            needsClinicians,
            needsTentativeDate,
            readyForPromotion,
            fulfilled,
            canceled,
            oneOffs,
        } = workflowTabCounts;
        return {
            needsServices: needsServices.totalCount,
            needsClinicians: needsClinicians.totalCount,
            needsTentativeDate: needsTentativeDate.totalCount,
            readyForPromotion: readyForPromotion.totalCount,
            fulfilled: fulfilled.totalCount,
            canceled: canceled.totalCount,
            oneOffs: oneOffs.totalCount,
        };
    }, [workflowTabCounts]);
};

function countSuffix(count?: number) {
    if (count === undefined || count === 0) {
        return '';
    }
    return ` (${count})`;
}

export function VisitRequestQueueTabs() {
    const { selectedDateRangeFilter, dialogFilters } = useVisitRequestListPageContext();
    const { queueTab, setQueueTab } = useVisitRequestListPageContext();
    const perTabCounts = usePerTabCounts({
        additionalFilters: { ...selectedDateRangeFilter, ...dialogFilters },
    });
    return (
        <Tabs
            value={queueTab}
            variant={'scrollable'}
            scrollButtons={'auto'}
            onChange={(_event, tab: QueueTab) => setQueueTab(tab)}
        >
            <Tab
                label={`One-Offs${countSuffix(perTabCounts?.oneOffs)}`}
                value={QueueTab.ONE_OFF_VISIT_REQUESTS}
            />
            <Tab
                label={`${renderVisitRequestWorkflowStepAsAction(
                    VisitRequestWorkflowStep.NEEDS_SERVICES,
                    true,
                )}${countSuffix(perTabCounts?.needsServices)}`}
                value={QueueTab.NEED_SERVICES}
            />
            <Tab
                label={`${renderVisitRequestWorkflowStepAsAction(
                    VisitRequestWorkflowStep.NEEDS_CLINICIAN,
                    true,
                )}${countSuffix(perTabCounts?.needsClinicians)}`}
                value={QueueTab.NEED_CLINICIANS}
            />
            <Tab
                label={`${renderVisitRequestWorkflowStepAsAction(
                    VisitRequestWorkflowStep.NEEDS_TENTATIVE_DATE,
                    true,
                )}${countSuffix(perTabCounts?.needsTentativeDate)}`}
                value={QueueTab.NEED_TENTATIVE_DATE}
            />
            <Tab
                label={`${renderVisitRequestWorkflowStepAsAction(
                    VisitRequestWorkflowStep.READY_FOR_PROMOTION,
                    true,
                )}${countSuffix(perTabCounts?.readyForPromotion)}`}
                value={QueueTab.READY_FOR_PROMOTION}
            />
            <Tab
                label={`Fulfilled${countSuffix(perTabCounts?.fulfilled)}`}
                value={QueueTab.FULFILLED}
            />
            <Tab
                label={`Canceled${countSuffix(perTabCounts?.canceled)}`}
                value={QueueTab.CANCELED}
            />
        </Tabs>
    );
}
